import { Link } from '@tanstack/react-router';
import { FolderOpen, ImageIcon, Pencil, Trash2 } from 'lucide-react';
import type { AlbumListItem } from '@/lib/api';

interface AlbumCardProps {
  album: AlbumListItem;
  onEdit?: (album: AlbumListItem) => void;
  onDelete?: (album: AlbumListItem) => void;
}

export function AlbumCard({ album, onEdit, onDelete }: AlbumCardProps) {
  const formattedDate = new Date(album.createdAt * 1000).toLocaleDateString();

  return (
    <div className="group relative overflow-hidden rounded-lg border bg-card transition-all hover:shadow-md">
      <Link to="/albums/$albumId" params={{ albumId: album.id }} className="block">
        {/* Cover Image */}
        <div className="relative aspect-[4/3] bg-muted">
          {album.coverPhotoUrl ? (
            <img
              src={album.coverPhotoUrl}
              alt={album.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <FolderOpen className="h-16 w-16 text-muted-foreground/50" />
            </div>
          )}
          {/* Photo count badge */}
          <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-xs text-white">
            <ImageIcon className="h-3 w-3" />
            <span>{album.photoCount}</span>
          </div>
        </div>

        {/* Album Info */}
        <div className="p-3">
          <h3 className="font-medium text-foreground line-clamp-1">{album.name}</h3>
          {album.description && (
            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{album.description}</p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">{formattedDate}</p>
        </div>
      </Link>

      {/* Actions Menu */}
      {(onEdit || onDelete) && (
        <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <div className="flex gap-1">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(album);
                }}
                className="rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
                aria-label="アルバムを編集"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(album);
                }}
                className="rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-destructive"
                aria-label="アルバムを削除"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
